
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PieChart } from "@/components/ui/chart";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCampaignStats, CampaignStats } from "@/hooks/useNewsletterStats";
import { format } from "date-fns";
import { Download } from "lucide-react";

// Use the Campaign type from useCampaigns.ts to avoid type conflicts
import type { Campaign } from "@/hooks/useCampaigns";

interface EmailCampaignSummaryProps {
  selectedCampaignId: string | null;
  campaigns: Campaign[];
  dateRange: { from: Date; to: Date };
}

export function EmailCampaignSummary({ 
  selectedCampaignId, 
  campaigns,
  dateRange 
}: EmailCampaignSummaryProps) {
  const { data: campaignStats, isLoading } = useCampaignStats(selectedCampaignId);
  
  // Filter campaigns by date range
  const filteredCampaigns = campaigns.filter(campaign => {
    if (!campaign.sent_at) return false;
    const sentDate = new Date(campaign.sent_at);
    return sentDate >= dateRange.from && sentDate <= dateRange.to;
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="w-full h-[300px]" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="w-full h-[100px]" />
          <Skeleton className="w-full h-[100px]" />
          <Skeleton className="w-full h-[100px]" />
        </div>
      </div>
    );
  }

  if (selectedCampaignId) {
    // Show individual campaign stats
    return <SingleCampaignStats 
      campaignStats={campaignStats} 
      campaign={campaigns.find(c => c.id === selectedCampaignId)}
    />;
  }

  // No campaign selected - show summary for all campaigns in date range
  return <AllCampaignsStats campaigns={filteredCampaigns} dateRange={dateRange} />;
}

function SingleCampaignStats({ 
  campaignStats, 
  campaign 
}: { 
  campaignStats: CampaignStats | null;
  campaign: Campaign | undefined;
}) {
  if (!campaignStats || !campaign) {
    return (
      <div className="text-center py-8 text-slate-500">
        No data available for this campaign
      </div>
    );
  }

  const sentDate = campaign.sent_at ? new Date(campaign.sent_at) : null;

  // Prepare data for pie chart
  const engagementData = {
    labels: ['Opened', 'Clicked', 'Not Opened'],
    datasets: [
      {
        data: [
          campaignStats.opened - campaignStats.clicked, 
          campaignStats.clicked,
          campaignStats.delivered - campaignStats.opened
        ],
        backgroundColor: [
          'rgba(99, 102, 241, 0.7)',  // indigo for opened
          'rgba(16, 185, 129, 0.7)',  // green for clicked
          'rgba(203, 213, 225, 0.7)'  // slate for not opened
        ],
        borderColor: [
          'rgb(99, 102, 241)',
          'rgb(16, 185, 129)',
          'rgb(203, 213, 225)'
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{campaign.name}</h3>
          <p className="text-sm text-slate-600">"{campaign.subject}"</p>
          {sentDate && (
            <p className="text-xs text-slate-500 mt-1">
              Sent on {format(sentDate, 'PPP')} at {format(sentDate, 'p')}
            </p>
          )}
        </div>
        <Badge className={
          campaign.status === 'sent' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : 
          campaign.status === 'sending' ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' :
          'bg-slate-100 text-slate-800 hover:bg-slate-100'
        }>
          {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-slate-600 mb-1">Delivery Rate</div>
            <div className="text-3xl font-bold text-slate-900">
              {(campaignStats.delivered / campaignStats.total_sends * 100).toFixed(1)}%
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {campaignStats.delivered} delivered of {campaignStats.total_sends} sent
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-slate-600 mb-1">Open Rate</div>
            <div className="text-3xl font-bold text-slate-900">
              {campaignStats.open_rate.toFixed(1)}%
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {campaignStats.opened} opened of {campaignStats.delivered} delivered
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-slate-600 mb-1">Click Rate</div>
            <div className="text-3xl font-bold text-slate-900">
              {campaignStats.click_rate.toFixed(1)}%
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {campaignStats.clicked} clicked of {campaignStats.opened} opened
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-medium text-slate-900">Engagement Breakdown</h4>
            <Button variant="outline" size="sm" className="h-8">
              <Download className="h-3.5 w-3.5 mr-1" />
              Export
            </Button>
          </div>
          <div className="h-[300px]">
            <PieChart 
              data={engagementData}
              options={{
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'right'
                  }
                }
              }}
              className="w-full h-full"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AllCampaignsStats({ 
  campaigns,
  dateRange
}: { 
  campaigns: Campaign[];
  dateRange: { from: Date; to: Date };
}) {
  if (campaigns.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <p>No campaigns sent during selected date range</p>
        <p className="text-sm mt-2">
          {format(dateRange.from, 'PPP')} - {format(dateRange.to, 'PPP')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-lg font-medium text-slate-900">
        {campaigns.length} campaigns sent between {format(dateRange.from, 'PP')} and {format(dateRange.to, 'PP')}
      </h3>
      
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Subject</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Sent</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {campaigns.map(campaign => (
              <tr key={campaign.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-900">{campaign.name}</td>
                <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">{campaign.subject}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                  {campaign.sent_at 
                    ? format(new Date(campaign.sent_at), 'PPP') 
                    : "Not sent"}
                </td>
                <td className="px-4 py-3">
                  <Badge className={
                    campaign.status === 'sent' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : 
                    campaign.status === 'sending' ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' :
                    'bg-slate-100 text-slate-800 hover:bg-slate-100'
                  }>
                    {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
